import Link from 'next/link';
import { useState } from 'react';

import Button from '@/components/global/button';
import Icon from '@/components/global/icon';
import IndexShareModal from '@/components/global/index-share-modal';
import Graph from '@/components/graph/index-graph';
import IndexFollowButton from '@/components/index/follow-button';
import { NormalizeIndexData } from '@/components/index/helpers';
import PerformanceChange from '@/components/products/performance-change';
import { CalcPercentDiff, RoundToDecimals } from '@/helpers/numbers';
import { GetIndexSymbolPublicPath } from '@/helpers/paths';
import cn from '@/lib/cn';

type Props = {
    index: {
        id: string;
        name: string;
        symbol: string;
        threeMonthDailySnapshots: Array<{
            asOfDate: string;
            netAssetValue: string;
            isBackcalculated: boolean;
        }>;
        followCount: number;
    };
};

const IndexStripe = ({ index: { id, name, symbol, threeMonthDailySnapshots, followCount } }: Props) => {
    const { normalizedIndexData, symbolList } = NormalizeIndexData(threeMonthDailySnapshots, symbol);
    let graph = null;
    let indexChange = null;
    const [showShareModal, setShowShareModal] = useState(false);

    if (normalizedIndexData.length) {
        const firstItem = normalizedIndexData[0];
        const lastItem = normalizedIndexData[normalizedIndexData.length - 1];
        // calculate the total change since inception for the index
        indexChange = CalcPercentDiff(firstItem.symbolValues[symbol], lastItem.symbolValues[symbol]);
        let color = '#02D8C3';
        if (indexChange < 0) {
            color = '#D80275';
        }

        graph = (
            <Graph
                indexData={normalizedIndexData}
                symbolList={symbolList}
                height={120}
                showTooltip={false}
                showLegend={false}
                showXLabels={false}
                showYLabels={false}
                cursor="pointer"
                lineColor={color}
            />
        );
    }

    const followButton = (
        <div className="flex shrink-0 flex-col justify-between">
            <div>
                <IndexShareModal
                    isOpen={showShareModal}
                    closeFn={() => setShowShareModal(false)}
                    indexSymbol={symbol}
                />
                <Button
                    className="text-xs !text-brand-blue-yves mr-0 ml-auto"
                    type="action"
                    shadow={false}
                    onClick={() => setShowShareModal(true)}
                    color="transparent"
                    textPosition="right"
                >
                    <Icon
                        type="shareArrow"
                        size="xs"
                        className="mr-1"
                        color="blueYves"
                    />
                    Share
                </Button>
            </div>

            <div className={cn('flex justify-between items-center')}>
                <span className="text-brand-unicorn font-brand-md mr-4">
                    {RoundToDecimals(0, followCount)} Following
                </span>
                <IndexFollowButton
                    indexId={id}
                    indexName={name}
                />
            </div>
        </div>
    );

    const title = <div className="text-brand-navy font-brand-bold text-left text-2xl mb-2">{name}</div>;

    return (
        <div className="flex flex-row bg-white justify-between p-4 mb-6 h-auto shadow-md border border-brand-gray">
            <div>
                <Link
                    href={GetIndexSymbolPublicPath(symbol)}
                    className="cursor-pointer no-underline flex-grow"
                >
                    {title}
                    <div>
                        {graph ? (
                            <div className="mb-2 border border-brand-gray-med py-2 w-64">{graph}</div>
                        ) : (
                            <div className="w-64 h-[120px] bg-brand-gray flex justify-center items-center">
                                <Icon
                                    type="indexDraft"
                                    color="gray"
                                />
                            </div>
                        )}
                    </div>
                </Link>
                {indexChange && (
                    <div className="mb-4 flex items-end">
                        <PerformanceChange percentChange={indexChange} />
                        <span className="ml-2 text-xs text-brand-purple pb-[4px]">3 months</span>
                    </div>
                )}
            </div>

            {followButton}
        </div>
    );
};

export default IndexStripe;
